import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
	addToQueue,
	getQueue,
	removeProcessedRequest,
	removeQueueItemsWhere,
} from './requestQueue';
import { isOfflineModeEntitled } from './offlineEntitlement';
import { getNetworkStatus } from './networkManager';
import { probeServerReachable } from './serverReachability';
import { getSessionScopeIdFromStore } from './sessionScope';
import {
	applyPendingSessionContextSwitch,
	clearPendingSessionContextSwitch,
} from './companyContextSwitch';

const HELD_QUEUE_KEY = 'offlineQueueHeld';

/** Held / conflicted offline mutations awaiting user reconciliation. */
export async function getHeldQueue(): Promise<any[]> {
	try {
		const raw = await AsyncStorage.getItem(HELD_QUEUE_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

/** Match backend dataItemConcurrency 1s tolerance. */
export const timestampsMatch = (
	clientTs?: string | Date | null,
	serverTs?: string | Date | null
): boolean => {
	if (!clientTs || !serverTs) return true;
	const clientMs = new Date(clientTs).getTime();
	const serverMs = new Date(serverTs).getTime();
	if (Number.isNaN(clientMs) || Number.isNaN(serverMs)) return true;
	return Math.abs(clientMs - serverMs) <= 1000;
};

export type QueuedPreflightConflict = {
	request: any;
	reason: 'version_mismatch' | 'item_missing';
	serverItem?: any;
	clientValues?: any;
	itemId?: string;
	operation?: 'update' | 'delete';
};

const extractDataItemIdFromRequest = (request: any): string | undefined => {
	const url = String(request?.url || '');
	const deleteMatch = url.match(/\/data\/delete-item\/([^/?]+)/i);
	if (deleteMatch?.[1]) return deleteMatch[1];

	const body = request?.data;
	if (body?.dataId) return String(body.dataId);
	return undefined;
};

const extractExpectedUpdatedAt = (request: any): string | undefined => {
	const body = request?.data;
	if (body?.expectedUpdatedAt) {
		return new Date(body.expectedUpdatedAt).toISOString();
	}
	const header = request?.headers?.['x-expected-updated-at'];
	if (header) return new Date(header).toISOString();
	const params = request?.params?.expectedUpdatedAt;
	if (params) return new Date(params).toISOString();
	return undefined;
};

/**
 * Before replaying the offline queue, check whether the server already has
 * newer versions of items we intend to mutate.
 */
export async function detectQueuedPreflightConflicts(
	queue: any[]
): Promise<QueuedPreflightConflict[]> {
	if (!queue.length) return [];

	let getDataItemById: ((id: string) => Promise<any>) | undefined;
	try {
		const apiModule = await import('../api');
		getDataItemById = apiModule.getDataItemById;
	} catch {
		return [];
	}

	const conflicts: QueuedPreflightConflict[] = [];

	for (const request of queue) {
		const url = String(request?.url || '').toLowerCase();
		const method = String(request?.method || 'get').toLowerCase();
		const isDataMutation =
			url.includes('/data/update-item') ||
			url.includes('/data/delete-item');

		if (!isDataMutation || !['post', 'delete', 'patch'].includes(method)) {
			continue;
		}

		const itemId = extractDataItemIdFromRequest(request);
		const expectedUpdatedAt = extractExpectedUpdatedAt(request);
		if (!itemId || !expectedUpdatedAt) continue;

		try {
			const serverItem = await getDataItemById!(itemId);
			if (!serverItem) {
				conflicts.push({
					request,
					reason: 'item_missing',
					itemId,
					operation: url.includes('delete') ? 'delete' : 'update',
					clientValues: request?.data?.values,
				});
				continue;
			}

			if (
				!timestampsMatch(expectedUpdatedAt, serverItem?.updatedAt)
			) {
				conflicts.push({
					request,
					reason: 'version_mismatch',
					serverItem,
					clientValues: request?.data?.values,
					itemId,
					operation: url.includes('delete') ? 'delete' : 'update',
				});
			}
		} catch (error) {
			if (__DEV__) {
				console.warn('[syncCoordinator] preflight skipped for', itemId, error);
			}
		}
	}

	return conflicts;
}

export async function holdConflictedQueueRequests(
	conflicts: QueuedPreflightConflict[]
): Promise<void> {
	if (!conflicts.length) return;

	const AsyncStorage = (await import('@react-native-async-storage/async-storage'))
		.default;
	const existingRaw = await AsyncStorage.getItem(HELD_QUEUE_KEY);
	const held = existingRaw ? JSON.parse(existingRaw) : [];

	for (const conflict of conflicts) {
		const url = String(conflict.request?.url || '');
		const method = String(conflict.request?.method || '');
		await removeQueueItemsWhere(
			(req) =>
				String(req?.url || '') === url &&
				String(req?.method || '') === method &&
				JSON.stringify(req?.data) === JSON.stringify(conflict.request?.data)
		);
		held.push({
			...conflict.request,
			heldAt: Date.now(),
			conflictReason: conflict.reason,
			conflictItemId: conflict.itemId,
		});
	}

	await AsyncStorage.setItem(HELD_QUEUE_KEY, JSON.stringify(held));
}

export async function publishPreflightConflicts(
	conflicts: QueuedPreflightConflict[]
): Promise<void> {
	if (!conflicts.length) return;

	try {
		const { publishDataItemConflict } = await import(
			'@utils/query/dataItemConflictBus'
		);

		for (const conflict of conflicts) {
			if (conflict.reason !== 'version_mismatch' || !conflict.serverItem) {
				continue;
			}
			publishDataItemConflict({
				conflict: true,
				code: 'DATA_ITEM_VERSION_CONFLICT',
				operation: conflict.operation || 'update',
				message:
					'Server data changed while you were offline. Choose which version to keep.',
				serverItem: conflict.serverItem,
				clientValues: conflict.clientValues,
				itemId: conflict.itemId,
				folderId: conflict.serverItem?.dataId
					? String(conflict.serverItem.dataId)
					: undefined,
			});
		}
	} catch (error) {
		console.warn('[syncCoordinator] failed to publish preflight conflicts', error);
	}
}

let replayInFlight = false;

/**
 * Replay queued offline mutations after connectivity + server are back.
 * Preflight-conflicted items are held for user resolution instead of blindly applied.
 */
export async function replayOfflineQueue(): Promise<void> {
	if (replayInFlight) return;
	if (!getNetworkStatus()) return;
	if (!isOfflineModeEntitled()) return;

	const serverUp = await probeServerReachable(5000, true);
	if (!serverUp) return;

	replayInFlight = true;

	try {
		await applyPendingSessionContextSwitch();

		const currentScope = getSessionScopeIdFromStore();
		const queue = await getQueue();
		const scopedQueue = queue.filter(
			(item) => (item.sessionScopeId ?? currentScope) === currentScope
		);

		if (!scopedQueue.length) return;

		const preflightConflicts = await detectQueuedPreflightConflicts(scopedQueue);
		if (preflightConflicts.length > 0) {
			await holdConflictedQueueRequests(preflightConflicts);
			await publishPreflightConflicts(preflightConflicts);
		}

		let remaining = await getQueue();

		while (remaining.length > 0) {
			const request = remaining[0];
			const requestScope = request.sessionScopeId ?? currentScope;
			if (requestScope !== currentScope) {
				break;
			}

			try {
				const idempotencyKey =
					request.idempotencyKey ||
					`${request.method || 'get'}-${request.url}-${Date.now()}`;
				const sanitizedConfig = {
					url: request.url,
					method: request.method || 'get',
					...(request.data != null && { data: request.data }),
					...(request.params != null && { params: request.params }),
					...(request.headers != null && {
						headers: {
							...request.headers,
							'x-idempotency-key': idempotencyKey,
						},
					}),
					withCredentials: request.withCredentials ?? true,
					...(request.baseURL != null && { baseURL: request.baseURL }),
				};

				await axios(sanitizedConfig);
				await removeProcessedRequest();
			} catch (err: any) {
				const status = err?.response?.status;
				const conflictData = err?.response?.data;

				if (
					status === 409 &&
					(conflictData?.conflict === true ||
						conflictData?.code === 'DATA_ITEM_VERSION_CONFLICT')
				) {
					try {
						const {
							publishDataItemConflict,
							extractDataItemConflict,
						} = await import('@utils/query/dataItemConflictBus');
						const conflict = extractDataItemConflict(err);
						if (conflict) {
							publishDataItemConflict(conflict);
						}
					} catch (publishErr) {
						console.warn('Failed to publish data item conflict:', publishErr);
					}
					await holdConflictedQueueRequests([
						{
							request,
							reason: 'version_mismatch',
							serverItem: conflictData?.serverItem,
							clientValues: conflictData?.clientValues,
							itemId: extractDataItemIdFromRequest(request),
							operation: conflictData?.operation,
						},
					]);
					continue;
				}

				const transient =
					!status ||
					status >= 500 ||
					err?.code === 'ERR_NETWORK' ||
					err?.code === 'ECONNABORTED';

				if (transient) {
					break;
				}

				// Drop permanently invalid entries (404, 400) to unblock the queue
				await removeProcessedRequest();
			}

			remaining = await getQueue();
		}
	} finally {
		replayInFlight = false;
	}
}

/** Re-queue a held request after the user resolves a conflict (push). */
export async function requeueHeldRequest(request: any): Promise<void> {
	await addToQueue(request);
	const AsyncStorage = (await import('@react-native-async-storage/async-storage'))
		.default;
	const raw = await AsyncStorage.getItem(HELD_QUEUE_KEY);
	if (!raw) return;
	const held = JSON.parse(raw);
	const next = held.filter(
		(item: any) =>
			!(
				String(item?.url) === String(request?.url) &&
				JSON.stringify(item?.data) === JSON.stringify(request?.data)
			)
	);
	await AsyncStorage.setItem(HELD_QUEUE_KEY, JSON.stringify(next));
}
